import type { FC, ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Tooltip from '@mui/material/Tooltip';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useCheckAuth } from 'features/login';
import { useGlobalSettings } from 'providers';
import { useLocationParams } from 'hooks';
import { APP_LIST, AppCode } from '../../constants';

import IconAll from 'assets/icon-all.svg?react';
import IconYg from 'assets/icon-yg.svg?react';
import IconFc from 'assets/icon-fc.svg?react';
import IconMb from 'assets/icon-mb.svg?react';
import IconFe from 'assets/icon-fe.svg?react';
import IconOm from 'assets/icon-om.svg?react';
import IconWf from 'assets/icon-wf.svg?react';
import IconFt from 'assets/icon-ft.svg?react';
import IconRl from 'assets/icon-rl.svg?react';
import IconFm from 'assets/icon-fm.svg?react';
import IconMi from 'assets/icon-mi.svg?react';

const APP_ICONS: Record<AppCode, ReactElement> = {
  [AppCode.YOGA_GO]: <IconYg width={26} />,
  [AppCode.FEAT_COACH]: <IconFc width={26} />,
  [AppCode.MUSCLE_BOOSTER]: <IconMb width={26} />,
  [AppCode.FASTEASY]: <IconFe width={26} />,
  [AppCode.OMO]: <IconOm width={26} />,
  [AppCode.WALKFIT]: <IconWf width={26} />,
  [AppCode.FOTTUR]: <IconFt width={26} />,
  [AppCode.RELIFY]: <IconRl width={26} />,
  [AppCode.FEMIA]: <IconFm width={26} />,
  [AppCode.MINDEA]: <IconMi width={26} />,
};

const getIcon = (code?: AppCode | ''): ReactElement => {
  if (code && APP_ICONS[code]) return APP_ICONS[code];

  return <IconAll  />;
};

const ApplicationButton: FC = () => {
  const { isSuperuser, apps } = useCheckAuth();
  const { selectedApp, setSelectedApp } = useGlobalSettings();
  const { app: appParam, setApp: setAppParam } = useLocationParams();

  const options = useMemo(
    () => APP_LIST.filter((app) => isSuperuser || apps?.includes(app.code)),
    [isSuperuser, apps],
  );

  const isAppParamAccessible = options.some((app) => app.code === appParam);

  // The URL param defines the app (deep links, browser back/forward) as long
  // as the user has access to it.
  useEffect(() => {
    if (!appParam || !isAppParamAccessible || appParam === selectedApp) return;

    setSelectedApp(appParam);
  }, [appParam, isAppParamAccessible, selectedApp, setSelectedApp]);

  const [open, setOpen] = useState(false);
  const [shake, setShake] = useState(false);
  const [noAccessDialogOpen, setNoAccessDialogOpen] = useState(true);
  const shakeTimeout = useRef<number | undefined>(undefined);

  const hasNoAccess = !isSuperuser && !options.length;

  const currentValue = options.some((app) => app.code === selectedApp)
    ? selectedApp
    : '';

  const mustChooseDefault = !currentValue && !isSuperuser && options.length > 1;

  useEffect(() => {
    if (!options.length || currentValue) return;
    // A valid URL param takes priority over the auto-selected default.
    if (appParam && isAppParamAccessible) return;

    if (isSuperuser || options.length === 1) {
      setSelectedApp(options[0].code);
    }
  }, [options, currentValue, appParam, isAppParamAccessible, isSuperuser, setSelectedApp]);

  useEffect(() => {
    if (!mustChooseDefault) return;

    const frameId = window.requestAnimationFrame(() => {
      setOpen(true);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [mustChooseDefault]);

  useEffect(
    () => () => {
      if (shakeTimeout.current) window.clearTimeout(shakeTimeout.current);
    },
    [],
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    if (!mustChooseDefault) {
      setOpen(false);

      return;
    }

    // A default is still required - nudge the user with a quick shake.
    setShake(true);
    if (shakeTimeout.current) window.clearTimeout(shakeTimeout.current);
    shakeTimeout.current = window.setTimeout(() => {
      setShake(false);
    }, 450);
  }, [mustChooseDefault]);

  const handleChange = useCallback(
    (event: SelectChangeEvent<AppCode | ''>) => {
      const value = event.target.value;
      if (value) {
        setSelectedApp(value as AppCode);
        setAppParam(value as AppCode);
        setOpen(false);
      }
    },
    [setSelectedApp, setAppParam],
  );

  if (hasNoAccess) {
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Tooltip title="No application assigned" placement="right">
            <Box
              component="button"
              type="button"
              aria-label="Application access unavailable"
              onClick={() => {
                setNoAccessDialogOpen(true);
              }}
              sx={{
                width: 42,
                height: 42,
                p: 0,
                border: 'none',
                borderRadius: 1.5,
                backgroundColor: 'transparent',
                cursor: 'pointer',
                opacity: 0.4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& svg': { width: 28, height: 28 },
              }}
            >
              <IconAll />
            </Box>
          </Tooltip>
        </Box>
        <Dialog
          container={() => document.getElementById('root') ?? document.body}
          open={noAccessDialogOpen}
          onClose={() => {
            setNoAccessDialogOpen(false);
          }}
          aria-labelledby="no-app-access-title"
        >
          <DialogTitle
            id="no-app-access-title"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'error.main',
            }}
          >
            <WarningAmberOutlinedIcon fontSize="small" color="error" />
            No application assigned
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              You don&apos;t have any application assigned to your account yet. 
              Please contact your manager to set up a project before you can create ads.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setNoAccessDialogOpen(false);
              }}
            >
              Got it
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  if (!options.length) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        '&:hover .app-caret': { opacity: 1 },
      }}
    >
      <FormControl>
        <MuiSelect<AppCode | ''>
          inputProps={{ IconComponent: () => null, 'aria-label': 'Application' }}
          variant="outlined"
          id="global-application-select"
          value={currentValue}
          onChange={handleChange}
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          displayEmpty
          MenuProps={{
            container: () => document.getElementById('root') ?? document.body,
            PaperProps: {
              sx: {
                '@keyframes appShakeX': {
                  '0%, 100%': { transform: 'translateX(0)' },
                  '20%, 60%': { transform: 'translateX(-7px)' },
                  '40%, 80%': { transform: 'translateX(7px)' },
                },
                animation: shake ? 'appShakeX 0.4s ease' : 'none',
              },
            },
          }}
          sx={{
            width: 42,
            maxWidth: 42,
            minWidth: 42,
            height: 42,
            borderRadius: 1.5,
            backgroundColor: 'transparent',
            transition: 'background-color 0.2s ease',
            '& [role="combobox"].MuiSelect-select.MuiSelect-outlined': {
              p: 0,
              width: 44,
              maxWidth: 44,
              minWidth: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            '& .MuiOutlinedInput-notchedOutline': { border: 'none', boxShadow: 'none' },
            '& svg': { width: 28, height: 28, transition: 'transform 0.2s ease' },
            '&:hover': { backgroundColor: 'rgba(41, 121, 255, 0.08)' },
            '&:hover svg': { transform: 'scale(0.80)' },
          }}
          renderValue={(code) => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {getIcon(code)}
            </Box>
          )}
        >
          {options.map(({ name, code }) => (
            <MenuItem key={`applicationButton.${code}`} value={code} sx={{ minWidth: 200, gap: 1.25 }}>
              <ListItemIcon sx={{ minWidth: 'auto' }}>{getIcon(code)}</ListItemIcon>
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </MuiSelect>
      </FormControl>
      <KeyboardArrowDownIcon
        className="app-caret"
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: -2,
          transform: 'translateX(-50%)',
          fontSize: 14,
          color: 'text.secondary',
          pointerEvents: 'none',
          opacity: 0,
          transition: 'opacity 0.2s ease',
        }}
      />
    </Box>
  );
};

export default ApplicationButton;
